import { useState, useEffect, useCallback } from 'react'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { useNotification } from '@refinedev/core'
import { X, Monitor, Clock, Shield } from 'lucide-react'

interface Session {
  id: number
  ip_address: string | null
  user_agent: string | null
  created_at: string | null
  expires_at: string | null
  is_current: boolean
}

export function SessionsPage() {
  const { open } = useNotification()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await flaskApi.get('/auth/sessions')
      setSessions(data)
    } catch {
      open?.({ type: 'error', message: 'Failed to load sessions' })
    } finally {
      setLoading(false)
    }
  }, [open])

  useEffect(() => { fetchSessions() }, [fetchSessions])

  const revokeSession = async (id: number) => {
    try {
      await flaskApi.delete(`/auth/sessions/${id}`)
      setSessions((prev) => prev.filter((s) => s.id !== id))
      open?.({ type: 'success', message: 'Session revoked' })
    } catch {
      open?.({ type: 'error', message: 'Failed to revoke session' })
    }
  }

  const revokeAllOthers = async () => {
    const others = sessions.filter((s) => !s.is_current)
    for (const s of others) {
      try { await flaskApi.delete(`/auth/sessions/${s.id}`) } catch { /* skip */ }
    }
    setSessions((prev) => prev.filter((s) => s.is_current))
    open?.({ type: 'success', message: `Revoked ${others.length} session(s)` })
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString()
  }

  const shortUA = (ua: string | null) => {
    if (!ua) return '—'
    if (ua.length > 60) return ua.slice(0, 60) + '…'
    return ua
  }

  if (loading) {
    return <div className="p-8 text-slate-500 text-sm">Loading sessions…</div>
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100">Active Sessions</h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage where you&apos;re signed in. Revoke any session you don&apos;t recognize.
          </p>
        </div>
        {sessions.filter((s) => !s.is_current).length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={revokeAllOthers}
            className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
          >
            Revoke all others
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {sessions.map((session) => (
          <div
            key={session.id}
            className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <Monitor size={16} className="text-slate-500" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {session.ip_address || 'Unknown IP'}
                  </span>
                  {session.is_current && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#076698]/10 text-[#076698] text-[10px] font-bold">
                      <Shield size={10} />
                      This device
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 truncate mt-0.5">
                  {shortUA(session.user_agent)}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Clock size={10} />
                    {formatDate(session.created_at)}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    Expires {formatDate(session.expires_at)}
                  </span>
                </div>
              </div>
            </div>
            {!session.is_current && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => revokeSession(session.id)}
                className="flex-shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        ))}
      </div>

      {sessions.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">No active sessions.</p>
      )}
    </div>
  )
}
